import React, { useRef, useCallback } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { Link, useNavigate, useOutletContext, useLocation } from 'react-router-dom';
import { getApi } from '../services/apiServices';
import { useState, useEffect } from 'react';
// import { fetchCountriesEsim } from '../Utils/helper';
import { toast } from "react-toastify";
import Modal from 'react-modal';
import { useLanguage } from '../contexts/LanguageContext';
import {
  PinIcon, SearchIcon, PhoneIcon, GlobeIcon,
  InfinityIcon, SignalIcon, CheckIcon, QrIcon, WifiIcon,
  BoltIcon, ShieldIcon, HeadsetIcon, PlaneIcon, DataStackIcon
} from './r4l/icons';
import Scribble from './r4l/Scribble';
import { getDestinationCardImage } from '../Utils/destinationImages';
import '../styles/r4l-home.css';

const TESTIMONIALS = [
  ['sophia', 'harry_casual_woman_cosmopolitan_ethnic_international_travelling_profile_6d46a6c7-b595-4237-8429-4e491c62d5aa.png'],
  ['isabella', 'harry_generate_an_image_of_the_face_of_a_hr_director_1fef7f79-11fb-4d67-943a-e0d84c820250.png'],
  ['daniel', 'harry_business_man_cosmopolitan_ethnic_international_travelling_profile_7b23aebe-9d31-46d0-a581-ff4129371a02.png'],
  ['james', 'harry_male_international_traveler_profile_image_for_a_client_testimonia_f41a6af9-6f00-415b-899f-c30304b16e2d.png'],
  ['emma', 'harry_generate_an_image_of_the_face_of_a_hr_director_a5683d00-b116-4276-8c8b-d85748950c18.png'],
  ['lily', 'harry_casual_student_woman_cosmopolitan_ethnic_international_travelling_7605963f-3a10-49e5-acc0-1b245db397ff.png'],
  ['steve', 'harry_young_business_man_cosmopolitan_ethnic_indian_international_trave_d8d3cb86-2447-4ede-957e-07717cd1dc4d.png'],
  ['benjamin', 'harry_young_smiling_casual_male_cosmopolitan_asian_ethnic_international_00cb7566-01bb-4282-8372-fa133a002c04.png'],
  ['alexander', 'harry_young_business_man_cosmopolitan_ethnic_international_travelling_p_3251416e-657b-4499-a483-589e19e694a4.png']
];

function Home() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { openDeviceModal } = useOutletContext();
  const { currentLanguage } = useLanguage();
  const [countriesEsim, setCountriesEsim] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openFaqItems, setOpenFaqItems] = useState(new Set());
  const [isShowingChildDevices, setIsShowingChildDevices] = useState(false);
  const [deviceNavigationPath, setDeviceNavigationPath] = useState([]);
  const [compatibilityData, setCompatibilityData] = useState([]);
  const [compatibilityLoading, setCompatibilityLoading] = useState(false);
  const [blogs, setBlogs] = useState([]);
  const [blogsLoading, setBlogsLoading] = useState(true);
  const [video, setVideo] = useState(null);
  const [videoLoading, setVideoLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [showVideo, setShowVideo] = useState(false);

  // Language mapping function to convert app language codes to API language codes
  const getApiLanguageCode = (appLanguage) => {
    const languageMap = {
      'ar': 'ar',     // Arabic
      'da': 'da',     // Danish
      'de': 'de',     // German
      'en': 'en',     // English
      'es': 'es',     // Spanish
      'fr': 'fr',     // French
      'he': 'he',     // Hebrew
      'it': 'it',     // Italian
      'ja': 'ja',     // Japanese
      'ko': 'ko',     // Korean
      'nl': 'nl',     // Dutch
      'no': 'no',     // Norwegian
      'pl': 'pl',     // Polish
      'pt': 'pt',     // Portuguese
      'ru': 'ru',     // Russian
      'sv': 'sv',     // Swedish
      'tr': 'tr',     // Turkish
      'zh': 'zh_CN'   // Chinese (Simplified)
    };
    return languageMap[appLanguage] || 'en'; // Default to English if not found
  };

  const toggleFaqItem = (itemId) => {
    const newOpenItems = new Set(openFaqItems);
    if (newOpenItems.has(itemId)) {
      newOpenItems.delete(itemId);
    } else {
      newOpenItems.add(itemId);
    }
    setOpenFaqItems(newOpenItems);
  };

  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);

    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    if (query.trim().length >= 1) {
      const timeout = setTimeout(() => {
        performSearch(query);
        setShowDropdown(true);
      }, 300);
      setSearchTimeout(timeout);
    } else {
      setSearchResults([]);
      setShowDropdown(false);
    }
  };

  const performSearch = async (query) => {
    try {
      setSearchLoading(true);
      const apiLanguage = getApiLanguageCode(currentLanguage);
      const response = await getApi(`/api/countries?search=${encodeURIComponent(query)}&limit=10&lang=${apiLanguage}`);

      if (response?.status && response?.data?.countries) {
        setSearchResults(response.data.countries);
      } else {
        setSearchResults([]);
      }
    } catch (error) {
      console.error('Error searching countries:', error);
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleCountrySelect = (country) => {
    setSearchQuery(country.name);
    setShowDropdown(false);
    setSearchResults([]);
    navigate(`/destination-detail/${country.iso3}`, { state: { countryData: country } });
  };

  const handleSearchSubmit = () => {
    if (searchQuery.trim()) {
      navigate(`/destination?search=${encodeURIComponent(searchQuery)}`);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearchSubmit();
    }
  };

  const closeDropdown = () => {
    setTimeout(() => {
      setShowDropdown(false);
    }, 200);
  };

  const fetchBlogs = async () => {
    try {
      setBlogsLoading(true);
      const response = await getApi('/api/blogs');

      if (response?.status && response?.data?.blogs) {
        const activeBlogs = response.data.blogs
          .filter(blog => blog.status === 1)
          .slice(0, 3);
        setBlogs(activeBlogs);
      } else {
        setBlogs([]);
      }
    } catch (error) {
      console.error('Error fetching blogs:', error);
      setBlogs([]);
    } finally {
      setBlogsLoading(false);
    }
  };

  const fetchVideo = async (language = currentLanguage) => {
    try {
      setVideoLoading(true);
      const apiLanguage = getApiLanguageCode(language);
      const response = await getApi(`/api/videos?lang=${apiLanguage}`);

      if (response?.status && response?.data) {
        // Handle different possible response structures:
        // - response.data could be an array of videos
        // - response.data could be a single video object
        // - response.data.videos could be an array
        let videoData = null;

        if (Array.isArray(response.data)) {
          videoData = response.data[0]; // Take first video if array
        } else if (response.data.videos && Array.isArray(response.data.videos)) {
          videoData = response.data.videos[0]; // Take first video from videos array
        } else if (response.data.video) {
          videoData = response.data; // Single video object
        }

        setVideo(videoData);
      } else {
        setVideo(null);
      }
    } catch (error) {
      console.error('Error fetching video:', error);
      setVideo(null);
    } finally {
      setVideoLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });
    } catch (error) {
      return dateString;
    }
  };

  const truncateDescription = (description, maxLength = 180) => {
    if (!description) return '';
    if (description.length <= maxLength) return description;
    return description.substring(0, maxLength).trim() + '...';
  };

  const truncateHtmlDescription = (htmlContent, maxLength = 180) => {
    if (!htmlContent) return '';


    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlContent;


    const textContent = tempDiv.textContent || tempDiv.innerText || '';

    if (textContent.length <= maxLength) {
      return htmlContent;
    }


    const truncatedText = textContent.substring(0, maxLength).trim() + '...';
    return truncatedText;
  };

  const closeDeviceModal = () => {
    setIsShowingChildDevices(false);
    setDeviceNavigationPath([]);
  };

  const handleDeviceClick = async (device) => {
    await fetchChildDevices(device.id);

    setDeviceNavigationPath(prev => [...prev, device]);
    setIsShowingChildDevices(true);
  };

  const handleBackToParent = async () => {
    if (deviceNavigationPath.length > 1) {
      const newPath = deviceNavigationPath.slice(0, -1);
      const previousDevice = newPath[newPath.length - 1];

      setDeviceNavigationPath(newPath);

      await fetchChildDevices(previousDevice.id);
    } else {
      setIsShowingChildDevices(false);
      setDeviceNavigationPath([]);
      await fetchCompatibilityData();
    }
  };

  const handleBackToRoot = async () => {
    setIsShowingChildDevices(false);
    setDeviceNavigationPath([]);
    await fetchCompatibilityData();
  };

  const handleDeviceSelection = async (device) => {
    try {
      setCompatibilityLoading(true);
      const response = await getApi(`/api/compatibilities/by-parent?parent_id=${device.id}`);

      if (response?.status && response?.data && response.data.length > 0) {
        const firstItem = response.data[0];
        if (firstItem.description && firstItem.name === null) {
          setCompatibilityData([firstItem]);
          setIsShowingChildDevices(true);
          setDeviceNavigationPath(prev => [...prev, device]);
        } else {
          await handleDeviceClick(device);
        }
      } else {
        closeDeviceModal();
      }
    } catch (error) {
      console.error('Error checking device children:', error);
      closeDeviceModal();
    } finally {
      setCompatibilityLoading(false);
    }
  };

  const customStyles = {
    content: {
      top: '50%',
      left: '50%',
      right: 'auto',
      bottom: 'auto',
      marginRight: '-50%',
      transform: 'translate(-50%, -50%)',
      border: 'none',
      borderRadius: '10px',
      padding: '0',
      maxWidth: '600px',
      width: '90%',
      backgroundColor: 'transparent'
    },
    overlay: {
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      zIndex: 1000
    }
  };

  const fetchCompatibilityData = async () => {
    try {
      setCompatibilityLoading(true);
      const response = await getApi('/api/compatibilities');

      if (response?.status) {
        setCompatibilityData(response?.data || []);
      } else {
        console.error('Failed to fetch compatibility data:', response?.msg);
        toast.error(t('home.errors.failedToLoadCompatibility'));
      }
    } catch (error) {
      console.error('Error fetching compatibility data:', error);
      toast.error(t('home.errors.failedToLoadCompatibility'));
    } finally {
      setCompatibilityLoading(false);
    }
  };

  const fetchChildDevices = async (parentId) => {
    try {
      setCompatibilityLoading(true);
      const response = await getApi(`/api/compatibilities/by-parent?parent_id=${parentId}`);

      if (response?.status) {
        setCompatibilityData(response?.data || []);
        setIsShowingChildDevices(true);
      } else {
        console.error('Failed to fetch child devices:', response?.msg);
        toast.error(t('home.errors.failedToLoadChildDevices'));
      }
    } catch (error) {
      console.error('Error fetching child devices:', error);
      toast.error(t('home.errors.failedToLoadChildDevices'));
    } finally {
      setCompatibilityLoading(false);
    }
  };

  const [esimsDelivered, setEsimsDelivered] = useState(null);
  const [happyCustomers, setHappyCustomers] = useState(null);
  const [totalCountries, setTotalCountries] = useState(() => {
    const cached = Number(localStorage.getItem('totalCountries'));
    return cached > 0 ? cached : null;
  });
  const [selectedCurrency, setSelectedCurrency] = useState(localStorage.getItem('selectedCurrency') || 'USD');
  const [userChangedCurrency, setUserChangedCurrency] = useState(false);


  useEffect(() => {
    const fetchPopularCountriesAndStats = async () => {
      try {
        setLoading(true);
        setError(null);

        const currentCurrency = localStorage.getItem('selectedCurrency') || 'USD';
        const apiLanguage = getApiLanguageCode(currentLanguage);

        const currencyParam = currentCurrency !== 'USD' ? `&currency=${currentCurrency}` : '';
        const response = await getApi(`/api/countries?count=9&page=1&filter=popular${currencyParam}&lang=${apiLanguage}`);

        if (response?.status && response?.data?.countries) {
          setCountriesEsim(response.data.countries);
          if (response.data.total_countries > 0) {
            setTotalCountries(response.data.total_countries);
            localStorage.setItem('totalCountries', String(response.data.total_countries));
          }
          if (response.data.esims_delievered) {
            setEsimsDelivered(response.data.esims_delievered);
          }
          if (response.data.happy_customers) {
            setHappyCustomers(response.data.happy_customers);
          }
        } else {
          setCountriesEsim([]);
          setError('Failed to fetch popular countries');
        }
      } catch (error) {
        console.error('Error fetching popular countries:', error);
        setError('Failed to fetch popular countries');
        setCountriesEsim([]);
      } finally {
        setLoading(false);
      }
    };

    fetchPopularCountriesAndStats();
    fetchBlogs();
    fetchVideo();
  }, [selectedCurrency, userChangedCurrency, currentLanguage]);


  useEffect(() => {
    const handleCurrencyChange = () => {
      const currency = localStorage.getItem('selectedCurrency') || 'USD';
      setSelectedCurrency(currency);
      setUserChangedCurrency(true);
    };
    window.addEventListener('cartUpdated', handleCurrencyChange);
    return () => window.removeEventListener('cartUpdated', handleCurrencyChange);
  }, []);


  useEffect(() => {
    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [searchTimeout]);

  // Header links such as /#plans and /#how scroll to their section.
  const location = useLocation();
  useEffect(() => {
    if (!location.hash) return;
    const timer = setTimeout(() => {
      document.getElementById(location.hash.slice(1))?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 150);
    return () => clearTimeout(timer);
  }, [location.hash, loading]);

  const statValue = (value) => (value > 0 ? `${value}+` : '…');
  const countryCount = statValue(totalCountries);

  const faqItems = [
    { id: 'home-faq-1', q: 'faq.question1', a: t('faq.answer1') },
    { id: 'home-faq-2', q: 'faq.question2', a: <>{t('faq.answer2')} <Link onClick={openDeviceModal}>{t('faq.checkCompatibleDevices')}</Link></> },
    { id: 'home-faq-3', q: 'faq.question3', a: t('faq.answer3') },
    { id: 'home-faq-4', q: 'faq.question4', a: t('faq.answer4') },
    { id: 'home-faq-5', q: 'faq.question5', a: t('faq.answer5') },
    { id: 'home-faq-6', q: 'faq.question6', a: <Trans i18nKey="faq.answer6" components={{ 0: <a href="mailto:contact@roam4less.app" /> }} /> },
    { id: 'home-faq-7', q: 'faq.question7', a: <>{t('faq.answer7')} <Link to="/refund-policy">{t('faq.seeRefundPolicy')}</Link></> },
    { id: 'home-faq-8', q: 'faq.question8', a: t('faq.answer8') },
    { id: 'home-faq-11', q: 'faq.question11', a: t('faq.answer11') },
    { id: 'home-faq-12', q: 'faq.question12', a: <>{t('faq.answer12')} <Link to="/speedPolicy">{t('header.nav.speedPolicy')}</Link></> }
  ];

  const renderFaq = (item) => {
    const isOpen = openFaqItems.has(item.id);
    return (
      <div className="r4l-faq-item" key={item.id}>
        <button
          type="button"
          className="r4l-faq-q"
          onClick={() => toggleFaqItem(item.id)}
          aria-expanded={isOpen}
          aria-controls={`${item.id}-answer`}
        >
          {t(item.q)}
        </button>
        {isOpen && <div className="r4l-faq-a" id={`${item.id}-answer`}>{item.a}</div>}
      </div>
    );
  };

  /* ── Testimonials Carousel ─────────────────────────────────── */
  const [slideIndex, setSlideIndex] = useState(0);
  const touchStartX = useRef(null);
  const autoPlayRef = useRef(null);
  const total = TESTIMONIALS.length; // 9

  const clamp = (n) => ((n % total) + total) % total;

  const goTo = useCallback((n) => {
    setSlideIndex(clamp(n));
  }, [total]);

  const prev = useCallback(() => goTo(slideIndex - 1), [slideIndex, goTo]);
  const next = useCallback(() => goTo(slideIndex + 1), [slideIndex, goTo]);

  // Auto-play: advance every 5 s, pause on hover
  const startAuto = useCallback(() => {
    clearInterval(autoPlayRef.current);
    autoPlayRef.current = setInterval(() => setSlideIndex(i => clamp(i + 1)), 5000);
  }, []);
  const stopAuto = useCallback(() => clearInterval(autoPlayRef.current), []);

  useEffect(() => {
    startAuto();
    return stopAuto;
  }, [startAuto, stopAuto]);

  // Keyboard navigation when the section is focused
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'ArrowLeft') prev();
    if (e.key === 'ArrowRight') next();
  }, [prev, next]);

  // Touch swipe
  const onTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(dx) > 40) dx < 0 ? next() : prev();
    touchStartX.current = null;
  };

  const renderQuoteCard = ([key], posClass) => (
    <article className={`r4l-tcard ${posClass}`} key={key} aria-hidden={posClass === 'r4l-tcard--hidden'}>
      <header>
        {/* Audit: no stock or generated portraits; show initials unless an authentic photo is supplied */}
        <span className="r4l-tcard-initials" aria-hidden="true">
          {t(`testimonials.${key}.name`).split(/\s+/).map((part) => part[0]).join('').slice(0, 2).toUpperCase()}
        </span>
        <div>
          <b>{t(`testimonials.${key}.name`)}</b>
          <span>{t(`testimonials.${key}.location`)}</span>
          <div className="r4l-stars" aria-label="5 stars">★★★★★</div>
        </div>
      </header>
      <p>{t(`testimonials.${key}.text`)}</p>
    </article>
  );

  const plans = [
    {
      key: 'unlimited',
      image: 'images/r4l/plan-unlimited.webp',
      icon: <InfinityIcon />,
      title: t('Unlimited Plans'),
      tag: t('r4l.plans.unlimited.tag'),
      items: ['i1', 'i2', 'i3', 'i4'].map((k) => t(`r4l.plans.unlimited.${k}`)),
      cta: t('r4l.plans.unlimited.cta')
    },
    {
      key: 'data',
      featured: true,
      image: 'images/r4l/plan-bundles.webp',
      icon: <SignalIcon />,
      title: t('Data Bundles'),
      tag: t('r4l.plans.bundles.tag'),
      items: ['i1', 'i2', 'i3', 'i4', 'i5'].map((k) => t(`r4l.plans.bundles.${k}`, { count: countryCount })),
      cta: t('r4l.plans.bundles.cta')
    },
    {
      key: 'global',
      image: 'images/r4l/plan-global.webp',
      icon: <GlobeIcon />,
      title: t('r4l.plans.global.title'),
      tag: t('r4l.plans.global.tag'),
      items: ['i1', 'i2', 'i3', 'i4', 'i5'].map((k) => t(`r4l.plans.global.${k}`, { count: countryCount })),
      cta: t('r4l.plans.global.cta')
    }
  ];

  return (
    <div className="r4l-home">
      {/* Hero */}
      <section className="r4l-hero">
        <img className="r4l-hero-bg" src={window.getPublicUrl('images/r4l/hero.webp')} alt="" fetchpriority="high" />
        <div className="r4l-hero-shade" />
        <Scribble className="r4l-hero-note" text={t('scribbles.hero')} />
        <aside className="r4l-hero-chip">
          <span className="r4l-hero-chip-mark"><GlobeIcon /></span>
          <span>
            {t('promise.heroChip').split('\n').map((line, i) => (
              <React.Fragment key={i}>{i > 0 && <br />}{line}</React.Fragment>
            ))}
          </span>
        </aside>
        <div className="r4l-hero-inner">
          <div className="r4l-hero-copy">
            <h1>
              {t('r4l.hero.title1')}
              <span className="r4l-hero-accent">{t('r4l.hero.title2')}</span>
            </h1>
            <p className="r4l-lede">{t('r4l.hero.lede', { count: countryCount })}</p>
            <p className="r4l-hero-sub">{t('r4l.hero.sub')}</p>

            <div className="r4l-search" role="search">
              <PinIcon />
              <input
                type="text"
                placeholder={t('search.placeholder')}
                aria-label={t('search.placeholder')}
                value={searchQuery}
                onChange={handleSearchChange}
                onBlur={closeDropdown}
                onKeyPress={handleKeyPress}
              />
              <button type="button" className="r4l-search-go" onClick={handleSearchSubmit} aria-label="Search">
                <SearchIcon />
              </button>

              {showDropdown && searchLoading && searchResults.length === 0 && (
                <div className="r4l-suggest">
                  <div className="loader-overlay"><span className="loader"></span></div>
                </div>
              )}
              {showDropdown && !searchLoading && searchResults.length === 0 && searchQuery.trim().length >= 2 && (
                <div className="r4l-suggest">
                  <div className="r4l-suggest-empty">{t('No countries found for "{{query}}"', { query: searchQuery })}</div>
                </div>
              )}
              {showDropdown && searchResults.length > 0 && (
                <div className="r4l-suggest">
                  <ul>
                    {searchResults.map((country) => (
                      <li key={country.id} onClick={() => handleCountrySelect(country)}>
                        <img
                          src={country?.flag || window.getPublicUrl('images/earth-globe-global-svgrepo-com (1).svg')}
                          alt=""
                          onError={(e) => {
                            e.target.src = window.getPublicUrl('images/earth-globe-global-svgrepo-com (1).svg');
                          }}
                        />
                        {country.name}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <ul className="r4l-stats">
              <li><BoltIcon /><div><b>{t('r4l.hero.stat1Value')}</b><span>{t('r4l.hero.stat1Label')}</span></div></li>
              <li><GlobeIcon /><div><b>{countryCount}</b><span>{t('r4l.hero.stat2Label')}</span></div></li>
              <li><PhoneIcon /><div><b>{t('r4l.hero.stat3Value')}</b><span>{t('r4l.hero.stat3Label')}</span></div></li>
              <li><ShieldIcon /><div><b>{t('r4l.hero.stat4Value')}</b><span>{t('r4l.hero.stat4Label')}</span></div></li>
            </ul>
          </div>
        </div>
      </section>

      {/* Popular destinations */}
      <section
        className="r4l-band r4l-map-wash"
        id="destinations"
        style={{ '--r4l-map-img': `url("${window.getPublicUrl('images/r4l/world-map.webp')}")`, '--r4l-bg-leaves-img': `url("${window.getPublicUrl('images/r4l/bg-leaves.png')}")` }}
      >
        <div className="r4l-wrap">
          <div className="r4l-section-head">
            <div>
              <p className="r4l-kicker">{t('r4l.dest.kicker')}</p>
              <h2 className="r4l-block-title" dangerouslySetInnerHTML={{ __html: t('r4l.dest.title') }} />
              <p className="r4l-intro">{t('r4l.dest.sub')}</p>
            </div>
            <Scribble className="r4l-dest-note" text={t('scribbles.destinations')} />
          </div>
          <div className="r4l-dest-grid">
            {loading ? (
              <div className="r4l-dest-status"><span className="loader"></span></div>
            ) : error ? (
              <p className="r4l-dest-status">{error}</p>
            ) : (
              countriesEsim.slice(0, 6).map((country, index) => {
                const photo = getDestinationCardImage(country?.iso3);
                const flag = country?.flag || window.getPublicUrl('images/earth-globe-global-svgrepo-com (1).svg');
                return (
                  <article className="r4l-dest-card" key={index}>
                    <Link to={`/destination-detail/${country.iso3}`}>
                      <div className={`r4l-dest-photo${photo ? '' : ' is-flag'}`}>
                        <div className="r4l-dest-photo-clip">
                          <img
                            className="r4l-cover"
                            src={photo || flag.replace('/w80/', '/w320/')}
                            alt={`${country?.name} eSIM`}
                            loading="lazy"
                            onError={(e) => {
                              e.target.src = window.getPublicUrl('images/earth-globe-global-svgrepo-com (1).svg');
                            }}
                          />
                        </div>
                        {photo && <img className="r4l-flag" src={flag} alt="" />}
                      </div>
                      <div className="r4l-dest-meta">
                        <div>
                          <h3>{country?.name}</h3>
                          <p>{t('perGB')}: <b>{country?.symbol || '$'}{country?.lowest_rate || '49'}</b> {country?.currency || 'USD'}</p>
                        </div>
                        <span className="r4l-arrow" aria-hidden="true">→</span>
                      </div>
                    </Link>
                  </article>
                );
              })
            )}
          </div>
          <div className="r4l-dest-more">
            <Link className="r4l-btn-premium" to="/destination">
              {t('View all destinations')}<i aria-hidden="true"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* Plans */}
      <section className="r4l-band" id="plans" style={{ paddingTop: 20 }}>
        <div className="r4l-wrap r4l-plans-head">
          <p className="r4l-kicker">{t('r4l.plans.kicker')}</p>
          <h2 className="r4l-block-title" dangerouslySetInnerHTML={{ __html: t('r4l.plans.title') }} />
          <p className="r4l-intro">{t('r4l.plans.intro')}</p>
          <Scribble className="r4l-plans-note" text={t('scribbles.plans')} />
          <div className="r4l-plan-grid">
            {plans.map((plan) => (
              <article className={`r4l-plan-card${plan.featured ? ' is-featured' : ''}`} key={plan.key}>
                <div className="r4l-plan-shot">
                  <img src={window.getPublicUrl(plan.image)} alt="" loading="lazy" />
                </div>
                <div className="r4l-plan-body">
                  <span className="r4l-ico">{plan.icon}</span>
                  <h3>{plan.title}</h3>
                  <p className="r4l-tag">{plan.tag}</p>
                  <ul className="r4l-checks">
                    {plan.items.map((item, i) => (
                      <li key={i}><CheckIcon />{item}</li>
                    ))}
                  </ul>
                  <Link className={`r4l-btn-premium${plan.featured ? '' : ' is-solid'}`} to="/destination">
                    {plan.cta}<i aria-hidden="true"></i>
                  </Link>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Our promise: Data Bundles full-speed promise (client design, 16 Oct) */}
      <section className="r4l-promise" id="promise">
        <div className="r4l-wrap r4l-promise-grid">
          <div>
            <p className="r4l-kicker">{t('promise.kicker')}</p>
            <h2 className="r4l-block-title" dangerouslySetInnerHTML={{ __html: t('r4l.promise.title') }} />
            <p className="r4l-intro">{t('r4l.promise.intro')}</p>
            <ul className="r4l-promise-features">
              <li><BoltIcon /><b>{t('r4l.promise.f1Title')}</b><span>{t('r4l.promise.f1Sub')}</span></li>
              <li><DataStackIcon /><b>{t('r4l.promise.f2Title')}</b><span>{t('r4l.promise.f2Sub')}</span></li>
              <li><GlobeIcon /><b>{t('r4l.promise.f3Title', { count: countryCount })}</b><span>{t('r4l.promise.f3Sub')}</span></li>
              <li><HeadsetIcon /><b>{t('r4l.promise.f4Title')}</b><span>{t('r4l.promise.f4Sub')}</span></li>
            </ul>
            <div className="r4l-promise-compare">
              {[
                {
                  key: 'ours',
                  label: <b>{t('r4l.bundlePromise.oursLabel')}</b>,
                  value: <span className="r4l-promise-value is-ours"><strong>20GB</strong> {t('r4l.promise.oursValue')}</span>,
                  aria: `${t('r4l.bundlePromise.oursLabel')}: 20GB ${t('r4l.promise.oursValue')}`,
                  fast: 20,
                },
                {
                  key: 'other',
                  label: <b>{t('r4l.promise.otherLabel')} <span>{t('r4l.promise.illustrative')}</span></b>,
                  value: <span className="r4l-promise-value">{t('r4l.bundlePromise.otherValue')}</span>,
                  aria: `${t('r4l.promise.otherLabel')} ${t('r4l.promise.illustrative')}: ${t('r4l.bundlePromise.otherValue')}`,
                  fast: 5,
                },
              ].map((row) => (
                <div className="r4l-promise-row" key={row.key}>
                  <div className="r4l-promise-row-head">{row.label}{row.value}</div>
                  <div className="r4l-promise-bar" role="img" aria-label={row.aria}>
                    {Array.from({ length: 20 }, (_, i) => (
                      <i key={i} className={i < row.fast ? 'is-fast' : ''} />
                    ))}
                  </div>
                </div>
              ))}
              <p className="r4l-promise-note">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><circle cx="12" cy="12" r="9.5" /><path d="M12 11v6M12 7.5v.5" strokeLinecap="round" /></svg>
                <span>{t('r4l.bundlePromise.note')}</span>
              </p>
            </div>
            <div className="r4l-promise-actions">
              <Link className="r4l-btn r4l-promise-cta" to="/destination">
                {t('r4l.promise.ctaBundles')} <span aria-hidden="true">→</span>
              </Link>
              <div className="r4l-promise-assure">
                <ShieldIcon />
                <div><b>{t('r4l.promise.assureTitle')}</b><span>{t('r4l.promise.assureSub')}</span></div>
              </div>
            </div>
          </div>
          <div className="r4l-photo-panel">
            <img src={window.getPublicUrl('images/r4l/promise.webp')} alt="" loading="lazy" />
            <Scribble text={t('scribbles.promise')} />
            <div className="r4l-promise-float">
              <span className="r4l-promise-float-ico"><PlaneIcon /></span>
              <p>{t('r4l.promise.floatCard').split('\n').map((line, i) => <React.Fragment key={i}>{i > 0 && <br />}{line}</React.Fragment>)}</p>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="r4l-band" id="how" style={{ paddingTop: 10 }}>
        <div className="r4l-wrap r4l-steps-grid">
          <div>
            <p className="r4l-kicker">{t('r4l.how.kicker')}</p>
            <h2 className="r4l-block-title" dangerouslySetInnerHTML={{ __html: t('r4l.how.title') }} />
            <p className="r4l-intro">{t('r4l.how.intro')}</p>
            <div className="r4l-step-row">
              <article className="r4l-step">
                <div className="r4l-num"><SearchIcon /><b>1</b></div>
                <h3>{t('r4l.how.s1Title')}</h3>
                <p>{t('r4l.how.s1Text')}</p>
                <button type="button" className="r4l-link-arrow" onClick={openDeviceModal}>{t('Check compatible devices')}</button>
              </article>
              <article className="r4l-step">
                <div className="r4l-num"><QrIcon /><b>2</b></div>
                <h3>{t('r4l.how.s2Title')}</h3>
                <p>{t('r4l.how.s2Text')}</p>
                <Link className="r4l-link-arrow" to="/activateEsim">{t('howItWorks.checkInstructions')}</Link>
              </article>
              <article className="r4l-step">
                <div className="r4l-num"><WifiIcon /><b>3</b></div>
                <h3>{t('r4l.how.s3Title')}</h3>
                <p>{t('r4l.how.s3Text')}</p>
              </article>
            </div>
            <div className="r4l-app-card">
              <span className="r4l-app-card-icon"><PlaneIcon /></span>
              <div>
                <p className="r4l-kicker">{t('r4l.how.appKicker')}</p>
                <h3>{t('r4l.how.appTitle')}</h3>
                <p>{t('r4l.how.appText')}</p>
                <ul className="r4l-app-stores">
                  <li>
                    <a href="https://apps.apple.com/in/app/roam4less-esim-travel-data/id6756699233" target="_blank" rel="noopener noreferrer">
                      <img src={window.getPublicUrl('images/app-store.png')} alt={t('appDownloadModal.appStore')} />
                    </a>
                  </li>
                  <li>
                    <a href="https://play.google.com/store/apps/details?id=com.roam4less.app&hl=en_IN" target="_blank" rel="noopener noreferrer">
                      <img src={window.getPublicUrl('images/google-play.png')} alt={t('appDownloadModal.googlePlay')} />
                    </a>
                  </li>
                </ul>
              </div>
            </div>
          </div>
          <div className="r4l-steps-photo">
            {showVideo && video && video.video ? (
              <video
                controls
                autoPlay
                controlsList="nodownload"
                onContextMenu={(e) => e.preventDefault()}
              >
                <source src={`https://roam4less.app/backend${video.video}`} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            ) : (
              <>
                <img src={window.getPublicUrl('images/r4l/steps.webp')} alt="" loading="lazy" />
                <Scribble text={t('scribbles.steps')} />
                {!videoLoading && video && video.video && (
                  <button type="button" className="r4l-play" onClick={() => setShowVideo(true)} aria-label={t('r4l.nav.howItWorks')}>
                    <span aria-hidden="true"></span>
                  </button>
                )}
                <div className="r4l-float-card"><span>→</span>{t('r4l.how.floatCard')}</div>
              </>
            )}
          </div>
        </div>
      </section>

      {/* Download app */}
      <section className="r4l-band" id="app" style={{ paddingTop: 10 }}>
        <div className="r4l-wrap">
          <div
            className="r4l-app-band"
            style={{ '--r4l-app-img': `url("${window.getPublicUrl('images/r4l/app-band.webp')}")` }}
          >
            <div>
              <p className="r4l-kicker">{t('r4l.how.appKicker')}</p>
              <h2 className="r4l-block-title" dangerouslySetInnerHTML={{ __html: t('r4l.app.title') }} />
              <p className="r4l-intro">{t('r4l.app.intro')}</p>
              <div className="r4l-mini-points">
                <div><BoltIcon />{t('r4l.app.p1')}</div>
                <div><SignalIcon />{t('r4l.hero.stat1Value')} {t('r4l.hero.stat1Label')}</div>
                <div><GlobeIcon />{t('r4l.app.p3', { count: countryCount })}</div>
                <div><PhoneIcon />{t('r4l.app.p4')}</div>
              </div>
              <ul className="r4l-stores">
                <li>
                  <a href="https://apps.apple.com/in/app/roam4less-esim-travel-data/id6756699233" target="_blank" rel="noopener noreferrer">
                    <img src={window.getPublicUrl('images/app-store.png')} alt={t('appDownloadModal.appStore')} />
                  </a>
                </li>
                <li>
                  <a href="https://play.google.com/store/apps/details?id=com.roam4less.app&hl=en_IN" target="_blank" rel="noopener noreferrer">
                    <img src={window.getPublicUrl('images/google-play.png')} alt={t('appDownloadModal.googlePlay')} />
                  </a>
                </li>
              </ul>
              <Scribble className="r4l-app-bottom-note" text={t('scribbles.appBottom')} />
            </div>
            <div className="r4l-app-visual">
              <Scribble className="r4l-app-note" text={t('scribbles.appTop')} />
              <div className="r4l-phone" aria-hidden="true">
                <div className="r4l-phone-screen">
                  <div className="r4l-phone-top"><span>16:47</span><span>roam4less.app</span></div>
                  <div className="r4l-phone-body">
                    <div className="r4l-phone-hero" style={{ backgroundImage: `url("${window.getPublicUrl('images/r4l/dest-us.webp')}")` }}>
                      <span>{t('destinationDetail.eSIMFor', { country: 'United States' })}</span>
                    </div>
                    <span className="r4l-phone-ghost">{t('destinationDetail.checkCompatibility')}</span>
                    <div className="r4l-phone-pills">
                      <span>{t('destinationDetail.tabs.unlimitedPlans')}</span>
                      <span>{t('destinationDetail.tabs.dataBundles')}</span>
                      <span>{t('destinationDetail.tabs.globalESIM')}</span>
                    </div>
                    <p className="r4l-phone-title">{t('destinationDetail.descriptions.unlimitedPlans', { country: 'United States' })}</p>
                    <div className="r4l-phone-days">
                      {[3, 5, 7, 10, 15, 30].map((d) => (
                        <span key={d} className={d === 3 ? 'is-on' : ''}>{d} {t('cartDropdown.days')}</span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials carousel */}
      <section
        className="r4l-band r4l-testimonials-section"
        id="stories"
        style={{ paddingTop: 10 }}
        onMouseEnter={stopAuto}
        onMouseLeave={startAuto}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        onKeyDown={handleKeyDown}
        tabIndex={-1}
        aria-label="Customer testimonials"
      >
        <div className="r4l-wrap">
          <div className="r4l-testimonials-head">
            <div>
              <h2 className="r4l-block-title">{t('r4l.stories.title')}</h2>
              <p className="r4l-intro">{t('r4l.stories.sub')}</p>
            </div>
          </div>

          {/* Track with flanking arrows */}
          <div className="r4l-carousel-viewport">
            <button
              type="button"
              className="r4l-carousel-arrow r4l-carousel-arrow--prev"
              onClick={prev}
              aria-label="Previous testimonial"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="15 18 9 12 15 6"/></svg>
            </button>

            {/* 3 cards visible: prev | active (highlighted) | next */}
            <div className="r4l-carousel-track">
              {renderQuoteCard(TESTIMONIALS[clamp(slideIndex - 1)], 'r4l-tcard--prev')}
              {renderQuoteCard(TESTIMONIALS[clamp(slideIndex)],     'r4l-tcard--active')}
              {renderQuoteCard(TESTIMONIALS[clamp(slideIndex + 1)], 'r4l-tcard--next')}
            </div>

            <button
              type="button"
              className="r4l-carousel-arrow r4l-carousel-arrow--next"
              onClick={next}
              aria-label="Next testimonial"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="9 18 15 12 9 6"/></svg>
            </button>
          </div>

          {/* Dots */}
          <div className="r4l-carousel-dots" role="tablist" aria-label="Testimonial pagination">
            {TESTIMONIALS.map((_, i) => (
              <button
                key={i}
                type="button"
                role="tab"
                className={`r4l-dot${i === slideIndex ? ' is-active' : ''}`}
                aria-selected={i === slideIndex}
                aria-label={`Go to testimonial ${i + 1}`}
                onClick={() => goTo(i)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="r4l-band" id="faq" style={{ paddingTop: 10 }}>
        <div className="r4l-wrap">
          <div className="r4l-section-head">
            <div>
              <h2 className="r4l-block-title">{t('r4l.faq.title')}</h2>
              <p className="r4l-intro">{t('r4l.faq.sub')}</p>
            </div>
          </div>
          <div className="r4l-faq-grid">
            <div>{faqItems.slice(0, 5).map(renderFaq)}</div>
            <div>{faqItems.slice(5).map(renderFaq)}</div>
          </div>
        </div>
      </section>
    </div>
  );
}
export default Home;
